import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { getBannerList } from '@features/home/api/bannerService';
import { useLanguage } from '@shared/contexts/LanguageContext';
import 'swiper/css';
import 'swiper/css/pagination';

/**
 * 共用 Hero Banner 元件
 * 與首頁 LandingPage 的 banner 結構一致
 * 用於各子頁面頂部（報導、傳燈、線上祈福點燈等）
 */
const HeroBanner = ({ withNavbarOffset = false, navbarOffset = 70, onSlideChange, onBannersLoaded }) => {
    const [banners, setBanners] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const { currentLanguage } = useLanguage();
    const validBanners = banners.filter((b) => b?.desktopUrl || b?.mobileUrl);

    useEffect(() => {
        getBannerList()
            .then((data) => {
                setBanners(data);
                onBannersLoaded?.(data);
            })
            .catch((err) => {
                console.error('Failed to fetch banners:', err);
            })
            .finally(() => {
                setLoaded(true);
            });
    }, [currentLanguage.code]);

    return (
        <>
            <style>
                {`
                    .hero-banner-pagination {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                    .hero-banner-pagination .swiper-pagination-bullet {
                        width: 14px;
                        height: 14px;
                        background: rgba(120, 110, 100, 0.4);
                        opacity: 1;
                        transition: all 0.3s ease;
                    }
                    .hero-banner-pagination .swiper-pagination-bullet-active {
                        width: 20px;
                        height: 20px;
                        background: #786e64;
                        opacity: 1;
                    }
                    .hero-banner-section .swiper {
                        min-height: 0 !important;
                        min-width: 0 !important;
                        height: 100% !important;
                    }
                    .hero-banner-section .swiper-wrapper {
                        align-items: stretch !important;
                        height: 100% !important;
                        transition-timing-function: ease-in-out !important; 
                    }
                    .hero-banner-section .swiper-slide {
                        height: 100% !important;
                    }
                    .hero-banner-section .swiper-slide > div {
                        min-height: 100%;
                    }
                `}
            </style>
            {withNavbarOffset && <div style={{ height: navbarOffset }} />}
            <section className="relative w-full hero-banner-section md:[aspect-ratio:16/9]">
                <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    loop={validBanners.length > 1}
                    speed={650}
                    autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                    }}
                    navigation={{
                        nextEl: '.hero-banner-next',
                        prevEl: '.hero-banner-prev',
                    }}
                    pagination={{
                        el: '.hero-banner-pagination',
                        clickable: true,
                    }}
                    onSlideChange={(swiper) => onSlideChange?.(swiper.realIndex)}
                    className="w-full h-full"
                >
                    {validBanners.length > 0 ? validBanners.map((banner) => (
                        <SwiperSlide key={banner.id}>
                            <div className="relative w-full h-full" style={{ background: 'transparent' }}>
                                {banner.url ? (
                                    <a href={banner.url} className="block w-full h-full">
                                        <img
                                            alt="Banner"
                                            className="hidden md:block absolute left-0 top-0 w-full h-full object-cover object-top"
                                            src={banner.desktopUrl || banner.mobileUrl}
                                        />
                                        <img
                                            alt="Banner"
                                            className="w-full h-auto block md:hidden"
                                            src={banner.mobileUrl || banner.desktopUrl}
                                        />
                                    </a>
                                ) : (
                                    <>
                                        <img
                                            alt="Banner"
                                            className="hidden md:block absolute left-0 top-0 w-full h-full object-cover object-top"
                                            src={banner.desktopUrl || banner.mobileUrl}
                                        />
                                        <img
                                            alt="Banner"
                                            className="w-full h-auto block md:hidden"
                                            src={banner.mobileUrl || banner.desktopUrl}
                                        />
                                    </>
                                )}
                            </div>
                        </SwiperSlide>
                    )) : loaded ? null : (
                        <SwiperSlide>
                            <div className="relative w-full h-full" style={{ background: 'transparent' }} />
                        </SwiperSlide>
                    )}
                </Swiper>

                {/* Navigation Arrows - Desktop Only */}
                <button
                    className="hero-banner-prev hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 lg:w-14 lg:h-14 items-center justify-center bg-black/30 hover:bg-black/50 backdrop-blur-md rounded-full border border-white/20 text-white transition-all opacity-0 hover:opacity-100 z-10"
                >
                    <span className="material-symbols-outlined text-2xl lg:text-3xl">chevron_left</span>
                </button>
                <button
                    className="hero-banner-next hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 lg:w-14 lg:h-14 items-center justify-center bg-black/30 hover:bg-black/50 backdrop-blur-md rounded-full border border-white/20 text-white transition-all opacity-0 hover:opacity-100 z-10"
                >
                    <span className="material-symbols-outlined text-2xl lg:text-3xl">chevron_right</span>
                </button>

            </section>
            {/* Slider Dots - below banner */}
            <div className="hero-banner-pagination relative z-10 flex justify-center gap-2 mt-6 mb-4 md:mt-8 md:mb-6 lg:mt-10 lg:mb-8"></div>
        </>
    );
};

HeroBanner.propTypes = {
    withNavbarOffset: PropTypes.bool,
    onSlideChange: PropTypes.func,
    onBannersLoaded: PropTypes.func,
};

export default HeroBanner;
